import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'Douleutaras/redux/hooks';
import {
  getSellerServicePrices,
  selectServicesPricing,
  ServicesPricingState
} from 'Douleutaras/redux/data/seller/servicesPricingSlice';

import { hasRenderableCatalogue } from '../ServicesCatalogue/ServicesCatalogue';

export interface UseSellerServicePricesResult {
  servicesPricing: ServicesPricingState;
  areServicePriceFlagsReady: boolean;
  showServicesCatalogue: boolean;
}

/**
 * Owns the public, seller-scoped service-price listing for the viewed pro: its fetch (kept in sync
 * with the viewed username) and the two flags the profile derives from it.
 *
 * `showServicesCatalogue` keeps the Services tab/section visible while the listing loads or errors —
 * it hides only once we know the pro has no priced services — so the catalogue's skeleton and error
 * states stay reachable. (Otherwise a cold-load failure would silently hide the section.)
 */
export const useSellerServicePrices = (username?: string): UseSellerServicePricesResult => {
  const dispatch = useAppDispatch();
  const servicesPricing = useAppSelector(selectServicesPricing);

  const areServicePriceFlagsReady = Boolean(username)
    && servicesPricing.loaded
    && !servicesPricing.loading
    && servicesPricing.username === username;

  const showServicesCatalogue =
    hasRenderableCatalogue(servicesPricing.data) || servicesPricing.loading || servicesPricing.error;

  useEffect(() => {
    if (!username || servicesPricing.loading) {
      return;
    }
    if (servicesPricing.loaded && (servicesPricing.username ?? username) === username) {
      return;
    }
    dispatch(getSellerServicePrices({ username }));
  }, [username, servicesPricing.loaded, servicesPricing.loading, servicesPricing.username]);

  return { servicesPricing, areServicePriceFlagsReady, showServicesCatalogue };
};
